import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { getTripCountdown, TRIP_TARGET } from "../lib/api";
import { AlarmClock, Sailboat, X, Anchor } from "lucide-react";

const STORAGE_KEY = "tk_countdown_last_shown";
const HOUR_MS = 60 * 60 * 1000;
const ACCENT = "#4992ff";
const ACCENT_LIGHT = "#a5c8ff";

function useCountdown(intervalMs = 1000) {
  const [c, setC] = useState(() => getTripCountdown());
  useEffect(() => {
    const tick = () => setC(getTripCountdown());
    tick();
    const id = setInterval(tick, intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return c;
}

function MiniBlock({ value, label, testid }) {
  return (
    <div className="flex flex-col items-center px-2 py-1 rounded-lg min-w-[42px]" style={{ backgroundColor: "hsl(var(--surface-2))" }} data-testid={testid}>
      <div key={value} className="font-display font-bold tk-flip text-base leading-none tabular-nums" style={{ color: "hsl(var(--text-1))" }}>
        {String(value).padStart(2, "0")}
      </div>
      <div className="text-[9px] uppercase tracking-wider mt-0.5" style={{ color: "hsl(var(--text-2))" }}>{label}</div>
    </div>
  );
}

function BigBlock({ value, label, testid, accent = false }) {
  return (
    <div
      className="flex flex-col items-center justify-center rounded-2xl px-3 py-3 min-w-[68px] shadow-sm"
      style={{ backgroundColor: "white", border: `1px solid ${ACCENT}33` }}
      data-testid={testid}
    >
      <div key={value} className="font-display font-bold tk-flip text-3xl md:text-4xl leading-none tabular-nums" style={{ color: ACCENT }}>
        {String(value).padStart(2, "0")}
      </div>
      <div className="text-[10px] uppercase tracking-widest mt-2" style={{ color: accent ? ACCENT : "hsl(var(--text-2))" }}>
        {label}
      </div>
    </div>
  );
}

/* Single combined component: renders pills (returns inline) and popup overlay (portal-like fixed). */
export default function CountdownClock() {
  const [open, setOpen] = useState(false);
  // Live every second so header minute boundary changes visibly. Cheap.
  const c = useCountdown(1000);

  const openPopup = useCallback(() => {
    setOpen(true);
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
  }, []);

  // Auto-open every hour while app is open
  useEffect(() => {
    const checkAndMaybeOpen = () => {
      const last = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
      if (Date.now() - last >= HOUR_MS) {
        openPopup();
      }
    };
    checkAndMaybeOpen();
    const id = setInterval(checkAndMaybeOpen, 60 * 1000);
    return () => clearInterval(id);
  }, [openPopup]);

  const targetText = TRIP_TARGET.toLocaleString("tr-TR", {
    timeZone: "Europe/Istanbul",
    day: "2-digit", month: "long", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });

  return (
    <>
      {/* Header pills (clickable) */}
      {c.ended ? (
        <button
          type="button"
          onClick={openPopup}
          className="flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-lg"
          style={{ color: ACCENT, backgroundColor: "hsl(var(--surface-2))" }}
          data-testid="countdown-ended"
        >
          <Sailboat className="w-3.5 h-3.5" /> Yolculuk başladı!
        </button>
      ) : (
        <button
          type="button"
          onClick={openPopup}
          className="flex items-center gap-1.5 cursor-pointer hover:opacity-90 transition"
          data-testid="countdown-pills"
          title="22 Ağustos 2026, 15:00 (TR) — tıkla ayrıntıları gör"
        >
          <AlarmClock className="w-4 h-4" style={{ color: ACCENT }} />
          <MiniBlock value={c.days} label="Gün" testid="countdown-days" />
          <MiniBlock value={c.hours} label="Saat" testid="countdown-hours" />
          <MiniBlock value={c.minutes} label="Dk" testid="countdown-minutes" />
        </button>
      )}

      {/* Popup — rendered via portal so the header's backdrop-blur doesn't affect fixed positioning */}
      {open && createPortal(
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 tk-fade"
          style={{ backgroundColor: "rgba(8, 28, 64, 0.78)", backdropFilter: "blur(6px)" }}
          onClick={() => setOpen(false)}
          data-testid="countdown-popup"
        >
          <div
            className="tk-pop relative w-full max-w-md rounded-3xl overflow-hidden shadow-2xl"
            style={{ backgroundColor: "#ffffff" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* layered static wave with depth / shadow (no horizontal motion) */}
            <div className="absolute inset-x-0 top-0 h-32 overflow-hidden pointer-events-none">
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 600 200" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="wave-grad" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor={ACCENT_LIGHT} />
                    <stop offset="100%" stopColor={ACCENT} />
                  </linearGradient>
                  <filter id="wave-shadow" x="-10%" y="-10%" width="120%" height="140%">
                    <feGaussianBlur stdDeviation="3" />
                    <feOffset dy="3" />
                    <feComponentTransfer><feFuncA type="linear" slope="0.35" /></feComponentTransfer>
                    <feMerge>
                      <feMergeNode />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>
                {/* deepest back layer (darkest, least opaque) */}
                <path d="M0,100 C150,40 350,160 600,80 L600,0 L0,0 Z" fill={ACCENT} opacity="0.35" />
                {/* mid layer */}
                <path d="M0,130 C200,80 400,180 600,120 L600,0 L0,0 Z" fill="url(#wave-grad)" opacity="0.55" />
                {/* front crisp wave with soft drop-shadow */}
                <path d="M0,150 C160,100 360,210 600,150 L600,0 L0,0 Z" fill="url(#wave-grad)" filter="url(#wave-shadow)" />
                {/* highlight sheen on front wave */}
                <path d="M0,148 C160,98 360,208 600,148" stroke="white" strokeOpacity="0.5" strokeWidth="1.5" fill="none" />
              </svg>
              <Sailboat className="absolute left-1/2 top-3 -translate-x-1/2 w-12 h-12 tk-float" style={{ color: "white", filter: "drop-shadow(0 4px 6px rgba(0,0,0,0.25))" }} />
            </div>

            <button
              data-testid="countdown-popup-close"
              onClick={() => setOpen(false)}
              className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-white/85 hover:bg-white"
              style={{ color: "hsl(var(--text-2))" }}
            >
              <X className="w-4 h-4" />
            </button>

            <div className="relative pt-28 px-6 pb-6">
              <div className="text-center mb-1">
                <div className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: ACCENT }}>Mavi Yolculuk '26</div>
                <h3 className="font-display font-semibold text-xl leading-tight mt-1" style={{ color: "hsl(var(--text-1))" }}>Demir alıyoruz...</h3>
              </div>

              {c.ended ? (
                <p className="text-center text-sm mt-3" style={{ color: ACCENT }}>Yolculuk başladı! İyi seyirler.</p>
              ) : (
                <>
                  <div className="flex items-center justify-center gap-2 mt-4">
                    <BigBlock value={c.days} label="Gün" testid="popup-days" />
                    <BigBlock value={c.hours} label="Saat" testid="popup-hours" />
                    <BigBlock value={c.minutes} label="Dakika" testid="popup-minutes" />
                    <BigBlock value={c.seconds} label="Saniye" testid="popup-seconds" accent />
                  </div>
                  <div className="flex items-center justify-center gap-2 mt-5 text-[11px]" style={{ color: "hsl(var(--text-2))" }}>
                    <Anchor className="w-3.5 h-3.5" style={{ color: ACCENT }} />
                    <span>Hedef: <b style={{ color: "hsl(var(--text-1))" }}>{targetText}</b> · TR</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
